import { flags } from "@app/config";
import { dictionaries, type Key, type Locale } from "./dictionary";

export type { Key, Locale } from "./dictionary";

/** Every language the Activity has, in the order the picker lists them. */
export const LOCALES = Object.keys(dictionaries) as Locale[];

/**
 * The languages players may pick: Arabic waits behind its flag (decision 138), on in the test
 * build (`VITE_ARABIC`) so a native speaker can review it in Discord.
 */
export function availableLocales(
  arabic = flags.arabic?.enabled === true || import.meta.env.VITE_ARABIC === "true",
): Locale[] {
  return LOCALES.filter((locale) => locale !== "ar" || arabic);
}

/** The first of the device's languages the Activity speaks; English otherwise. */
export function detectLocale(
  languages: string | readonly string[],
  available: readonly Locale[] = availableLocales(),
): Locale {
  const list = typeof languages === "string" ? [languages] : languages;
  for (const language of list) {
    const base = language.toLowerCase().split("-")[0] as Locale;
    if (available.includes(base)) return base;
  }
  return "en";
}

/** Where the picked language is kept on this device (decision 133); never sent to the server. */
export const LOCALE_KEY = "hcc:locale";

/** The language to start in: the one picked on this device while still offered, else the device's. */
export function initialLocale(
  stored: string | null,
  languages: string | readonly string[],
  available: readonly Locale[] = availableLocales(),
): Locale {
  const picked = available.find((locale) => locale === stored);
  return picked ?? detectLocale(languages, available);
}

/** The locale for `Intl`: Arabic keeps Western digits (decision 131). */
export function intlLocale(locale: Locale): string {
  return locale === "ar" ? "ar-u-nu-latn" : locale;
}

const CATEGORY = /^(zero|one|two|few|many|other):/;
/** `{n|…}`: plural forms after a count; nothing else in a template has a `|` inside braces. */
const PLURAL = /\{(\w+)\|([^}]*)\}/g;

/**
 * The form for `count`: `one|other` for English (Turkish needs none after a number), or CLDR
 * categories by name for languages with more, `zero:…|one:…|two:…|few:…|many:…|other:…` (Arabic).
 */
export function pluralForm(locale: Locale, count: number, forms: string): string {
  const parts = forms.split("|");
  const category = new Intl.PluralRules(intlLocale(locale)).select(count);
  if (parts.some((part) => CATEGORY.test(part))) {
    const named = new Map(
      parts.map((part) => [part.slice(0, part.indexOf(":")), part.slice(part.indexOf(":") + 1)]),
    );
    return named.get(category) ?? named.get("other") ?? "";
  }
  return category === "one" ? (parts[0] ?? "") : (parts[1] ?? parts[0] ?? "");
}

const FSI = "\u2068";
const PDI = "\u2069";
const RLM = "\u200F";
const ARABIC_LETTER = /[\u0600-\u06FF\u0750-\u077F]/;
/** The first letter with a direction of its own; digits, marks and punctuation have none. */
const FIRST_STRONG = /[A-Za-z\u00C0-\u024F\u0600-\u06FF\u0750-\u077F]/;

/**
 * Arabic is not mirrored: each block of text takes its direction from its first strong letter
 * (ADR 0041). A sentence that starts with a player's Latin name would therefore lay its Arabic
 * out left to right (review 0027 G3). What is filled in is isolated (FSI … PDI, UAX #9), and a
 * sentence with Arabic in it that does not start in Arabic gets a right-to-left mark in front.
 */
function arabicOrder(text: string): string {
  if (!ARABIC_LETTER.test(text)) return text;
  const first = FIRST_STRONG.exec(text)?.[0] ?? "";
  return ARABIC_LETTER.test(first) ? text : `${RLM}${text}`;
}

/**
 * Fills `{name}` placeholders and `{name|…}` plurals. A missing key falls back to English, then
 * to the key itself.
 */
export function translate(
  locale: Locale,
  key: Key,
  params: Record<string, string | number> = {},
): string {
  const template = dictionaries[locale][key] ?? dictionaries.en[key] ?? key;
  const arabic = locale === "ar";
  const fill = (value: string | number) =>
    arabic && typeof value === "string" ? `${FSI}${value}${PDI}` : String(value);
  const filled = template
    .replace(PLURAL, (whole, name: string, forms: string) => {
      const value = params[name];
      return value === undefined ? whole : pluralForm(locale, Number(value), forms);
    })
    .replace(/\{(\w+)\}/g, (_, name: string) => {
      const value = params[name];
      return value === undefined ? `{${name}}` : fill(value);
    });
  return arabic ? arabicOrder(filled) : filled;
}

/** Server error codes map to sentences; unknown codes get the generic one with the code shown. */
export function translateError(locale: Locale, code: string): string {
  const key = `error.${code}` as Key;
  if (key in dictionaries.en) return translate(locale, key);
  return translate(locale, "error.generic", { code });
}
